/**
 * Fantasy Console 0 - Add File Modal
 *
 * Modal dialog for adding a new code file to the cart.
 */

import { useState, useRef, useEffect } from 'react'
import styles from './AddFileModal.module.css'
import modalStyles from '../../styles/modal.module.css'

interface AddFileModalProps {
  existingNames: string[]
  onConfirm: (fileName: string) => void
  onCancel: () => void
}

export function AddFileModal({
  existingNames,
  onConfirm,
  onCancel,
}: AddFileModalProps) {
  const [fileName, setFileName] = useState('')
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  const validateFileName = (name: string): string | null => {
    const trimmed = name.trim()

    if (!trimmed) {
      return 'File name cannot be empty'
    }

    if (trimmed.length > 30) {
      return 'File name too long (max 30 characters)'
    }

    // Check for invalid characters
    if (!/^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9]+)?$/.test(trimmed)) {
      return 'File name can only contain letters, numbers, underscores, and hyphens'
    }

    // Add .js extension if not present
    const finalName = trimmed.endsWith('.js') ? trimmed : `${trimmed}.js`

    // Check for duplicates
    if (
      existingNames.some((n) => n.toLowerCase() === finalName.toLowerCase())
    ) {
      return 'A file with this name already exists'
    }

    return null
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateFileName(fileName)
    if (validationError) {
      setError(validationError)
      return
    }

    const trimmed = fileName.trim()
    const finalName = trimmed.endsWith('.js') ? trimmed : `${trimmed}.js`
    onConfirm(finalName)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value)
    if (error) {
      setError('')
    }
  }

  return (
    <div className={modalStyles.modalOverlay} onClick={onCancel}>
      <div
        className={`${modalStyles.modalDialog} ${styles.modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`${modalStyles.modalTitle} ${styles.title}`}>
          Add New File
        </h2>

        <form onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label htmlFor='fileName' className={styles.label}>
              File Name
            </label>
            <input
              ref={inputRef}
              id='fileName'
              type='text'
              value={fileName}
              onChange={handleChange}
              placeholder='filename.js'
              className={`${styles.input} ${error ? styles.inputError : ''}`}
              autoComplete='off'
            />
            {error && <div className={styles.errorMessage}>{error}</div>}
            <p className={styles.hint}>
              .js extension will be added automatically if not provided
            </p>
          </div>

          <div className={`${modalStyles.modalActions} ${styles.actions}`}>
            <button
              type='button'
              onClick={onCancel}
              className={modalStyles.cancelButton}
            >
              Cancel
            </button>
            <button type='submit' className={modalStyles.confirmButton}>
              Add File
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
